import { useNotifications } from '@features/notifications/hooks/useNotifications';
import { useProfile } from '@shared/hooks/useProfile';
import { NotificationType } from '@shared/types/notification.types';
import { UserProfile } from '@shared/types/profile.types';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../../config/firebase';
import { Message, MessageType } from '../types';
import { useMessageUpdates } from './useMessageUpdates';

/**
 * Hook to manage messages in a conversation
 * Handles real-time updates, pagination, and message operations
 */
export const useMessages = (
  conversationId: string,
  selectedUser: UserProfile | null
) => {
  const { user } = useProfile();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const lastMessageRef = useRef<QueryDocumentSnapshot | null>(null);
  const isLoadingMoreRef = useRef(false);
  const messagesPerPage = 20;

  const { updateTypingStatus, addMessageUpdate, isTyping } = useMessageUpdates(
    conversationId,
    messages,
    setMessages
  );

  const { createNotification } = useNotifications();

  // Initial messages load and real-time updates
  useEffect(() => {
    // Reset state when conversation changes
    setMessages([]);
    setLoading(true);
    setError(null);
    setHasMore(true);
    lastMessageRef.current = null;
    isLoadingMoreRef.current = false;

    if (!conversationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Query for most recent messages
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(messagesPerPage)
    );

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const changes = snapshot.docChanges();

        // Process only added or modified messages
        changes.forEach((change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const newMessage = {
              id: change.doc.id,
              ...change.doc.data(),
              timestamp: change.doc.data().timestamp?.toDate(),
            } as Message;

            setMessages((prev) => {
              // Check if message already exists
              const messageIndex = prev.findIndex(
                (msg) => msg.id === newMessage.id
              );

              if (messageIndex !== -1) {
                // Update existing message
                const updatedMessages = [...prev];
                updatedMessages[messageIndex] = newMessage;
                return updatedMessages;
              } else {
                // Add new message
                return [...prev, newMessage].sort(
                  (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
                );
              }
            });

            // Mark message as read if it's not from current user
            if (!newMessage.read && newMessage.senderId !== user?.id) {
              updateDoc(
                doc(
                  db,
                  'conversations',
                  conversationId,
                  'messages',
                  newMessage.id
                ),
                { read: true }
              );
            }
          }
        });

        // Update pagination state
        if (snapshot.docs.length > 0) {
          lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];
          setHasMore(snapshot.docs.length === messagesPerPage);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error loading messages:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [conversationId, user?.id]);

  /**
   * Load older messages for pagination
   * Returns true if more messages were loaded
   */
  const loadMoreMessages = async () => {
    if (
      !hasMore ||
      !lastMessageRef.current ||
      loading ||
      isLoadingMoreRef.current
    ) {
      return false;
    }

    try {
      isLoadingMoreRef.current = true;

      const q = query(
        collection(db, 'conversations', conversationId, 'messages'),
        orderBy('timestamp', 'desc'),
        startAfter(lastMessageRef.current),
        limit(messagesPerPage)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        return false;
      }

      const olderMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
      })) as Message[];

      // Update last document reference
      lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];

      // Sort chronologically and merge with existing messages
      const sortedOlderMessages = olderMessages.sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );

      setMessages((prev) => {
        // Filter out any potential duplicates
        const existingIds = new Set(prev.map((msg) => msg.id));
        const uniqueNewMessages = sortedOlderMessages.filter(
          (msg) => !existingIds.has(msg.id)
        );

        if (uniqueNewMessages.length === 0) {
          setHasMore(false);
          return prev;
        }

        return [...uniqueNewMessages, ...prev];
      });

      setHasMore(snapshot.docs.length === messagesPerPage);
      return true;
    } catch (err: any) {
      console.error('Error loading more messages:', err);
      setError(err.message);
      return false;
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  const sendMessage = async (
    content: string,
    type: MessageType = 'text',
    mediaUrl?: string
  ) => {
    try {
      if (!user?.id) return;

      // Get conversation data to identify the receiver
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) return;

      const conversationData = conversationSnap.data();
      const receiverId = conversationData.participants.find(
        (id: string) => id !== user.id
      );

      // Create message
      await addDoc(
        collection(db, 'conversations', conversationId, 'messages'),
        {
          content,
          senderId: user.id,
          timestamp: Timestamp.now(),
          read: false,
          type,
          ...(mediaUrl && { mediaUrl }),
        }
      );

      // Send notification to receiver
      if (receiverId && selectedUser.settings?.notificationSettings?.messages) {
        await createNotification({
          receiverId,
          type: NotificationType.NEW_MESSAGE,
          metadata: {
            actorId: user.id,
            conversationId,
            messageType: type,
            preview: type === 'text' ? content.substring(0, 100) : `[${type}]`,
          },
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!conversationId || !user) return;

      try {
        const messageRef = doc(
          db,
          'conversations',
          conversationId,
          'messages',
          messageId
        );
        const messageDoc = await getDoc(messageRef);

        if (!messageDoc.exists()) {
          throw new Error('Message not found');
        }

        const messageData = messageDoc.data();
        if (messageData.senderId !== user.id) {
          throw new Error('You can only delete your own messages');
        }

        // Update message type to deleted
        await updateDoc(messageRef, {
          type: 'deleted',
          content: '',
          mediaUrl: null,
        });

        // Add update notification
        await addMessageUpdate(messageId, 'delete');

        // Update last message if needed
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.id === messageId) {
          await updateDoc(doc(db, 'conversations', conversationId), {
            lastMessage: {
              ...lastMessage,
              type: 'deleted',
              content: '',
              mediaUrl: null,
            },
          });
        }
      } catch (error: any) {
        console.error('Error deleting message:', error);
        throw error;
      }
    },
    [conversationId, user, messages, addMessageUpdate]
  );

  return {
    messages,
    loading,
    error,
    sendMessage,
    deleteMessage,
    loadMoreMessages,
    hasMore,
    updateTypingStatus,
    isTyping,
  };
};
